import React, { useMemo, useState } from 'react';

import { ArrowRight, Sparkles, MessageCircle, CreditCard, CheckCircle2, Search, PartyPopper, Presentation, Camera } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { useHashRouter } from '../hooks/useHashRouter';
import { useHomepageListings } from '../hooks/useHomepageListings';
import { CLS_CONTAINER, CATEGORY_CONFIG, INTENT_HUB_LINKS } from '../constants';
import { ListingCard } from '../components/ListingCard';
import { Seo } from '../components/Seo';
import { SmartImage } from '../components/SmartImage';
import { HeroEventsCollage, HeroEventsMobileStrip } from '../components/HeroEventsCollage';
// Lazy: the demand modal only opens on CTA click, but as a static import it
// dragged react-hook-form + the Google Maps vendor chunk into the entry
// bundle (audit: 426 kB unused JS on the homepage).
const DemandForm = React.lazy(() =>
  import('../components/demands/DemandForm').then(m => ({ default: m.DemandForm }))
);
import { trackEvent } from '../services/tracking';

export const Home: React.FC = () => {
  const { navigate } = useHashRouter();
  const { t } = useTranslation(["home", "common", "search", "listing", "profile", "add_listing", "admin"]);
  const [showDemandForm, setShowDemandForm] = useState(false);

  const handleDemandCTA = () => {
    trackEvent('cta_demand_clicked', { source: 'hero' });
    setShowDemandForm(true);
  };

  const { data: homepageData, isError } = useHomepageListings();
  const recentListings = homepageData?.listings || [];
  const allListingsCount = homepageData?.totalCount ?? null;

  const { averageRating, totalRatings } = useMemo(() => {
    const ratedSellers = recentListings.filter(l => l.seller.rating > 0);
    if (ratedSellers.length === 0) return { averageRating: null as number | null, totalRatings: 0 };
    return {
      averageRating: ratedSellers.reduce((acc, curr) => acc + curr.seller.rating, 0) / ratedSellers.length,
      totalRatings: ratedSellers.reduce((acc, curr) => acc + (curr.seller.ratingCount || 0), 0),
    };
  }, [recentListings]);

  return (
    <>
      <Seo
        title={t('seo_title', 'Priestory na oslavy a eventy na Slovensku | Espacero')}
        description={t('seo_desc', 'Nájdite dokonalý priestor na váš event, svadbu, narodeniny, meeting alebo kongres na Slovensku. Katalóg stoviek priestorov s technickými parametrami — rýchlo a bez poplatkov.')}
        schema={[
          // No Organization node here on purpose: index.html already emits the
          // canonical Organization + WebSite @graph site-wide. Restating it
          // created a second, @id-less copy — two entities instead of one.
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Najnovšie priestory na Espacero",
            "numberOfItems": recentListings.length,
            "itemListElement": recentListings.map((l, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "name": l.title,
              "url": `https://www.espacero.com/priestor/${l.slug || l.id}/`
            }))
          }
        ]}
        canonical="https://www.espacero.com/"
      />

      {/* ──────────────────────────────────────────────
          HERO — Warm Editorial Split Layout
          ────────────────────────────────────────────── */}
      <section className="hero-bg min-h-[480px] sm:min-h-[600px] relative flex items-center overflow-hidden">
        {/* Ambient gradient orbs */}
        <div className="hero-orb-1 animate-orb" />
        <div className="hero-orb-2" />

        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #33302A 1px, transparent 1px)',
            backgroundSize: '28px 28px'
          }}
        />

        <div className={`${CLS_CONTAINER} relative z-10 py-10 sm:py-16`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

            {/* Left: Text content */}
            <div className="text-left animate-fadeIn">

              {/* Badges row */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                {allListingsCount !== null && allListingsCount > 0 && (
                  <div className="stat-chip inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-stone-600 dark:text-stone-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-terra animate-pulse" />
                    {t('hero.stats_spaces', { count: allListingsCount, defaultValue: `${allListingsCount} priestorov` })}
                  </div>
                )}
                {averageRating !== null && totalRatings > 0 && (
                  <div className="stat-chip inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-stone-600 dark:text-stone-300">
                    <span className="text-amber-500">★</span>
                    {averageRating.toFixed(1)} · {t('listing:reviews_count', { count: totalRatings, defaultValue: `${totalRatings} hodnotení` })}
                  </div>
                )}
              </div>

              {/* Main headline - strictly 2 lines */}
              <h1 className="text-3xl sm:text-5xl md:text-5xl lg:text-6xl font-extrabold text-primary dark:text-stone-50 mb-4 leading-[1.12] tracking-tight">
                {t('hero.title_highlight', 'Priestory na oslavy, svadby a eventy') && (
                  <span className="block text-primary-gradient">{t('hero.title_highlight', 'Priestory na oslavy, svadby a eventy')}</span>
                )}
                <span className="block text-primary dark:text-stone-100">{t('hero.title', 'na Slovensku')}</span>
              </h1>

              <p className="text-base sm:text-lg text-stone-600 dark:text-stone-300 mb-8 max-w-lg leading-relaxed font-normal">
                <Trans i18nKey="hero.subtitle" ns="home">
                  Porovnajte priestory na meeting, workshop, fotenie alebo menší event. Stovky priestorov na{' '}
                  <span className="text-primary dark:text-stone-200 font-semibold">krátkodobý prenájom</span> s technickými parametrami na jednom mieste.
                </Trans>
              </p>

              {/* Highlighted CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
                <button
                  onClick={() => navigate('/market')}
                  className="flex-1 btn-primary-gradient px-6 py-4 rounded-2xl font-black text-sm sm:text-base text-white shadow-xl shadow-primary/20 hover:scale-[1.02] hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2.5 active:scale-95 group"
                >
                  <Search size={18} className="group-hover:scale-110 transition-transform shrink-0" />
                  <span>{t('hero.cta_explore', 'Nájsť priestor')}</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform shrink-0" />
                </button>
                <button
                  onClick={handleDemandCTA}
                  className="flex-1 px-6 py-4 rounded-2xl font-black text-sm sm:text-base text-primary dark:text-stone-100 bg-surface/90 dark:bg-stone-900/90 border-[1.5px] border-stone-300 dark:border-stone-700 shadow-lg hover:border-accent-terra hover:text-accent-terra transition-all duration-300 flex items-center justify-center gap-2.5 active:scale-95 group"
                >
                  <MessageCircle size={18} className="text-accent-terra group-hover:scale-110 transition-transform shrink-0" />
                  <span>Zadať dopyt</span>
                </button>
              </div>


              {/* Helper line */}
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-3 font-medium">
                {t('hero.helper_line', 'Oslavy, svadby, kongresy a eventy. Bez provízií a skrytých poplatkov.')}
              </p>

              {/* Mobile/tablet photo strip using real recent venue events */}
              <HeroEventsMobileStrip />
            </div>

            {/* Right: Candid event photo collage (desktop only, uses z-[15] and z-[25] stacking) */}
            <HeroEventsCollage allListingsCount={allListingsCount} />

          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────
          QUICK CATEGORY PILLS
          ────────────────────────────────────────────── */}
      <section className="py-4 sm:py-6 bg-white dark:bg-stone-900 border-b border-stone-100 dark:border-stone-800 transition-colors">
        <div className={CLS_CONTAINER}>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 md:justify-center">
            <button
              onClick={() => navigate('/market')}
              className="shrink-0 category-pill px-4 py-2 text-xs font-bold text-stone-600 dark:text-stone-300 dark:border-stone-700 transition-all"
            >
              {t('common:all')}
            </button>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => navigate('/market')}
                  className="shrink-0 category-pill px-4 py-2 text-xs font-bold text-stone-600 dark:text-stone-300 dark:border-stone-700 flex items-center gap-1.5 transition-all"
                >
                  <Icon size={12} style={{ color: config.color }} />
                  {t(`listing:categories.${config.id}`)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────
          3 ENTRY CARDS — "Find your space"
          ────────────────────────────────────────────── */}
      <section className="py-8 sm:py-14 transition-colors">
        <div className={CLS_CONTAINER}>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 sm:grid-cols-3 gap-3 sm:gap-5">
              {/* Jedna farba pre všetky vstupné karty — informácie kreslí ceruzka */}
              {[
                {
                  icon: PartyPopper,
                  title: t('entry_cards.card1_title', 'Oslavy a svadby'),
                  text: t('entry_cards.card1_text', 'Narodeniny, svadby, rodinné oslavy a večierky'),
                  path: '/market'
                },
                {
                  icon: Presentation,
                  title: t('entry_cards.card2_title', 'Business a kongres'),
                  text: t('entry_cards.card2_text', 'Kongresy, zasadačky, školenia a biznis stretnutia'),
                  path: '/market'
                },
                {
                  icon: Camera,
                  title: t('entry_cards.card3_title', 'Foto a video'),
                  text: t('entry_cards.card3_text', 'Ateliéry, štúdiá a priestory na content'),
                  path: '/market'
                }
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => navigate(item.path)}
                  className="flex flex-col items-center text-center p-4 sm:p-8 rounded-2xl sm:rounded-[32px] border border-stone-200 dark:border-stone-800 bg-surface dark:bg-stone-900 shadow-sm transition-all hover:shadow-card hover:-translate-y-1.5 hover:border-accent-terra/40 duration-300 group"
                >
                  <div className="shrink-0 w-12 h-12 sm:w-20 sm:h-20 bg-accent-terra/10 border border-accent-terra/30 rounded-xl sm:rounded-3xl mb-3 sm:mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:rotate-3">
                    <item.icon className="w-6 h-6 sm:w-10 sm:h-10 text-accent-terra" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold sm:font-extrabold text-primary dark:text-stone-100 text-[10px] sm:text-base mb-0.5 sm:mb-2 tracking-tight line-clamp-1">
                      {item.title}
                    </div>
                    <div className="hidden sm:block text-[11px] text-stone-500 dark:text-stone-400 font-medium leading-relaxed">
                      {item.text}
                    </div>
                  </div>
                  <div className="hidden sm:flex mt-4 items-center gap-1.5 text-accent-terra font-bold text-xs group-hover:gap-3 transition-all">
                    <span>{t('hero.cta_explore', 'Nájsť priestor')}</span>
                    <ArrowRight size={14} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────
          RECENT LISTINGS GRID
          ────────────────────────────────────────────── */}
      <section className="py-8 sm:py-12 bg-white dark:bg-stone-950 transition-colors">
        <div className={CLS_CONTAINER}>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="kota mb-2 max-w-[320px]"><i></i><span>{t('latest_spaces', 'Najnovšie priestory na eventy')}</span><i></i></div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-primary dark:text-stone-100 tracking-tight">
                Najnovšie priestory
              </h2>
            </div>
            <button
              onClick={() => navigate('/market')}
              className="flex items-center gap-1.5 text-sm font-bold text-accent-terra hover:opacity-80 transition-colors group"
            >
              {t('all', 'Všetky')}
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {isError ? (
            <div className="text-center py-12 bg-red-50/50 dark:bg-red-900/10 rounded-2xl border border-red-100 dark:border-red-900/30">
              <p className="text-red-500 font-medium">Nepodarilo sa načítať priestory. Skúste to prosím neskôr.</p>
            </div>
          ) : recentListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
              {recentListings.slice(0, 4).map((listing, i) => (
                <div
                  key={listing.id}
                  className="h-full transform transition-transform hover:scale-[1.01]"
                >
                  <ListingCard listing={listing} compact priority={i < 4} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-14 text-stone-500 text-sm border-2 border-dashed border-stone-200 dark:border-stone-800 rounded-3xl">
              {t('search:no_results', 'Žiadne priestory nevyhovujú zadaným kritériám.')}
            </div>
          )}
        </div>
      </section>

      {/* ──────────────────────────────────────────────
          HOW IT WORKS
          ────────────────────────────────────────────── */}
      <section className="py-10 sm:py-16 transition-colors">
        <div className={CLS_CONTAINER}>
          <div className="text-center mb-10">
            <div className="kota max-w-[280px] mx-auto mb-3"><i></i><span>Postup v troch krokoch</span><i></i></div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primary dark:text-stone-100 tracking-tight">
              {t('how_it_works.title', 'Ako si nájsť priestor')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Search,
                title: t('how_it_works.step1_title', '1. Vyberte si typ akcie'),
                desc: t('how_it_works.step1_desc', 'Oslava, svadba, meeting, kongres, workshop, foto/video alebo firemný večierok.'),
                accent: 'bg-accent-terra',
                image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600'
              },
              {
                icon: MessageCircle,
                title: t('how_it_works.step2_title', '2. Porovnajte priestory'),
                desc: t('how_it_works.step2_desc', 'Lokalita, kapacita, vybavenie a fotky na jednom mieste.'),
                accent: 'bg-accent-terra',
                image: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&q=80&w=600'
              },
              {
                icon: Sparkles,
                title: t('how_it_works.step3_title', '3. Rezervujte si termín'),
                desc: t('how_it_works.step3_desc', 'Spojte sa priamo s majiteľom a dohodnite si detaily bez sprostredkovateľov.'),
                accent: 'bg-accent-terra',
                image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=600'
              }
            ].map((step, idx) => (
              <div
                key={idx}
                onClick={() => navigate('/how-it-works')}
                className="relative overflow-hidden rounded-3xl bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 shadow-card transition-all hover:shadow-card-hover hover:-translate-y-1 duration-300 group cursor-pointer"
              >
                <div className="h-44 sm:h-52 relative overflow-hidden">
                  <SmartImage
                    src={step.image}
                    alt={step.title}
                    seoContext={{ title: step.title, category: 'Ako to funguje' }}
                    width={600}
                    height={400}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    containerClassName="w-full h-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  {/* Step number */}
                  <div className="absolute top-4 left-4 w-8 h-8 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                    <span className="text-white font-black text-sm">{idx + 1}</span>
                  </div>
                  {/* Step icon */}
                  <div className={`absolute bottom-4 left-5 w-10 h-10 rounded-2xl ${step.accent} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon size={20} strokeWidth={2.5} className="text-white" />
                  </div>
                </div>
                <div className="p-5 pt-4">
                  <h3 className="font-bold text-primary dark:text-stone-100 mb-1.5 text-base">{step.title}</h3>
                  <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────
          PRICING — Dark warm section
          ────────────────────────────────────────────── */}
      {/* Kompaktnejšia 0 € sekcia (audit: blok zaberal príliš veľa viewportu) */}
      <section className="section-parallax-bg py-8 sm:py-12 text-white relative overflow-hidden">
        {/* Subtle warm grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(217,164,65,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(217,164,65,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(217,164,65,0.12),transparent_60%)]" />

        <div className={`${CLS_CONTAINER} relative z-10`}>
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D9A441]/15 border border-[#D9A441]/30 text-[#D9A441] text-[10px] font-black uppercase tracking-widest mb-4">
              <CreditCard size={12} />
              {t('pricing.free_badge', 'Bez poplatkov a provízií')}
            </div>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              {t('pricing.title', 'Vždy férové podmienky')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {[
              { title: t('pricing.owner_title', 'Pre majiteľov priestorov'), desc: t('pricing.owner_desc', 'Navždy zadarmo pre majiteľov priestorov. Pridajte svoj priestor a získajte dopyty na oslavy aj biznis eventy bez provízií.') },
              { title: t('pricing.renter_title', 'Pre organizátorov'), desc: t('pricing.renter_desc', 'Hľadaj a porovnávaj priestory rýchlo. Žiadne servisné poplatky pre hľadajúcich.') }
            ].map((plan, idx) => (
              <div
                key={idx}
                onClick={() => navigate('/pricing')}
                className="bg-white/5 backdrop-blur-sm p-6 rounded-3xl border border-white/10 hover:border-white/20 relative overflow-hidden flex flex-col items-center text-center cursor-pointer hover:bg-white/8 transition-all duration-300 group"
              >
                <div className="mb-4 w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <CheckCircle2 size={26} className="text-[#D9A441]" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{plan.title}</h3>
                <p className="text-sm text-stone-500 mb-5 leading-relaxed">{plan.desc}</p>
                <div className="mt-auto">
                  <span className="text-4xl font-black text-white">0 €</span>
                  <span className="text-stone-500 text-sm ml-1">/mesiac</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────
          CATEGORY IMAGE GRID
          ────────────────────────────────────────────── */}
      <section className="py-6 sm:py-10 bg-white dark:bg-stone-950 transition-colors">
        <div className={CLS_CONTAINER}>
          <div className="mb-5">
            <h2 className="text-xl sm:text-2xl font-extrabold text-primary dark:text-stone-100 tracking-tight">
              {t('categories', 'Populárne kategórie')}
            </h2>
            <p className="text-stone-500 dark:text-stone-400 text-sm mt-1">{t('select_env', 'Vyberte si štýl vašej akcie')}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              const categoryImages: Record<string, string> = {
                'Foto/Video štúdio':    'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?w=400&h=300&fit=crop&q=80',
                'Event priestor':       'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=400&h=300&fit=crop&q=80',
                'Meeting/konferenčka':  'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=400&h=300&fit=crop&q=80',
                'Ateliér / dielňa':    'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop&q=80',
                'Exteriér':             'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=400&h=300&fit=crop&q=80',
                'Unikátny priestor':    'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300&fit=crop&q=80'
              };
              const categoryImage = categoryImages[key] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&q=80';

              return (
                <div
                  key={key}
                  onClick={() => navigate('/market')}
                  className="group relative h-32 sm:h-44 rounded-2xl sm:rounded-3xl overflow-hidden cursor-pointer shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300"
                >
                  <SmartImage
                    src={categoryImage}
                    alt={t(`listing:categories.${config.id}`)}
                    seoContext={{ title: t(`listing:categories.${config.id}`), category: t('listing:category') }}
                    priority={false}
                    width={400}
                    height={300}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    containerClassName="w-full h-full"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />

                  {/* Arrow */}
                  <div className="absolute top-2.5 right-2.5 bg-white/20 backdrop-blur-md p-1.5 rounded-xl text-white border border-white/20 group-hover:bg-accent-terra/90 group-hover:border-accent-terra/50 transition-all duration-300">
                    <ArrowRight size={12} />
                  </div>

                  {/* Label */}
                  <div className="absolute bottom-0 left-0 p-3 sm:p-4 w-full">
                    <div className={`inline-flex items-center justify-center p-1.5 rounded-lg ${config.bg} mb-1.5 shadow-sm`}>
                      <Icon size={11} style={{ color: config.color }} />
                    </div>
                    <h3 className="text-xs sm:text-sm font-bold text-white leading-tight line-clamp-1">
                      {t(`listing:categories.${config.id}`)}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Obľúbené výbery priestorov — čistá a uhladená navigácia */}
      <section className="py-8 sm:py-10 transition-colors border-t border-stone-100 dark:border-stone-800/80">
        <div className={CLS_CONTAINER}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <h2 className="text-sm font-bold text-stone-700 dark:text-stone-200 tracking-tight flex items-center gap-2">
              <Sparkles size={15} className="text-accent-terra shrink-0" />
              <span>{t('hub_links.picks_title', 'Obľúbené výbery priestorov')}</span>
            </h2>
            <span className="text-xs text-stone-600 dark:text-stone-300">
              Rýchly prístup k overeným priestorom podľa typu akcie
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {INTENT_HUB_LINKS.map(link => (
              <a
                key={link.to}
                href={link.to}
                onClick={(e) => { e.preventDefault(); navigate(link.to); }}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold text-stone-600 dark:text-stone-300 bg-stone-100/70 hover:bg-white dark:bg-stone-800/60 dark:hover:bg-stone-800 hover:text-accent-terra border border-stone-200/50 dark:border-stone-700/50 hover:border-accent-terra/40 hover:shadow-2xs transition-all duration-200"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {showDemandForm && (
        <React.Suspense
          fallback={
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-fadeIn">
              <div className="bg-white dark:bg-stone-900 rounded-2xl p-5 shadow-2xl flex items-center gap-3 border border-stone-200 dark:border-stone-800">
                <div className="w-5 h-5 border-2 border-accent-terra border-t-transparent rounded-full animate-spin" />
                <span className="text-xs sm:text-sm font-bold text-stone-700 dark:text-stone-200">Načítavam formulár...</span>
              </div>
            </div>
          }
        >
          <DemandForm onClose={() => setShowDemandForm(false)} />
        </React.Suspense>
      )}
    </>
  );
};
